import React, { useCallback, useMemo, useRef, useState } from "react";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import { Flex, Text } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import {
  AccountingPayableData,
  PaymentBatchStepNames,
  PaymentBatchWorkflow,
  TransactionsTable,
  TransactionRowItem,
  PaymentBatchRowItem,
} from "@jsq/payments-components";
import { useAppContext } from "~/contexts/appContext";
import { globalEnv } from "~/global-env";
import { hasTreasuryAccessOnAnyArena, isTreasuryUser } from "~/utils/userRoles";
import * as styles from "./styles_vanilla.css";

const TRANSACTIONS_BASE_URL = `${globalEnv.treasuryRoute}/transactions`;

type TransactionsPageParams = {
  transactionBatchId?: string;
  paymentBatchId?: string;
};

type ReviewWorkflowParams = {
  transactionBatchId: string;
  paymentBatchId: string;
};

const TransactionsPage: React.FC = () => {
  const history = useHistory();
  const { transactionBatchId, paymentBatchId } = useParams<TransactionsPageParams>();
  const [selectedPayeeCount, setSelectedPayeeCount] = useState<number | null>(null);
  const { user } = useAppContext();
  // AG Grid's onRowClicked fires at the native DOM level (row element), before React's synthetic
  // event delegation reaches the root. This means even though ActionButton calls
  // stopPropagation(), the row-click handler has already fired by the time React processes it.
  // When the Pay button is clicked both handlePayClick (React synthetic) and
  // handleSelectTransaction (AG Grid native) fire — with handlePayClick first. Without this
  // guard, handleSelectTransaction's subsequent history.push('/{id}') would overwrite the
  // '/{id}/pay' navigation, causing the slideout to open instead of PaymentBatchWorkflow.
  // The same applies to the Review action — without reviewClickGuardRef, the row-click handler
  // would overwrite the '/{id}/{paymentBatchId}/review' URL and only the L1 slideout would open.
  const payClickGuardRef = useRef(false);
  const reviewClickGuardRef = useRef(false);

  const payMatch = useRouteMatch({
    path: `${TRANSACTIONS_BASE_URL}/:transactionBatchId/pay`,
    exact: true,
  });

  const reviewMatch = useRouteMatch<ReviewWorkflowParams>({
    path: `${TRANSACTIONS_BASE_URL}/:transactionBatchId/:paymentBatchId/review`,
    exact: true,
  });

  const showPayWorkflow = !!payMatch;
  const showReviewWorkflow = !!reviewMatch;

  const handleSelectTransaction = useCallback(
    (transaction: TransactionRowItem) => {
      if (payClickGuardRef.current) {
        payClickGuardRef.current = false;
        return;
      }
      if (reviewClickGuardRef.current) {
        reviewClickGuardRef.current = false;
        return;
      }
      history.push(`${TRANSACTIONS_BASE_URL}/${transaction.id}`);
    },
    [history],
  );

  const handleCloseSlideout = useCallback(() => {
    history.push(TRANSACTIONS_BASE_URL);
  }, [history]);

  const handlePaymentBatchReviewClick = useCallback(
    (paymentBatch: PaymentBatchRowItem) => {
      if (!transactionBatchId || !paymentBatch.id) return;
      history.push(`${TRANSACTIONS_BASE_URL}/${transactionBatchId}/${paymentBatch.id}/review`);
    },
    [history, transactionBatchId],
  );

  const handleReviewClick = useCallback(
    (transaction: TransactionRowItem) => {
      reviewClickGuardRef.current = true;
      const firstPaymentBatchId = transaction.paymentBatchesForReview[0];
      if (firstPaymentBatchId) {
        history.push(
          `${TRANSACTIONS_BASE_URL}/${transaction.id}/${firstPaymentBatchId}/review`,
        );
      } else {
        history.push(`${TRANSACTIONS_BASE_URL}/${transaction.id}`);
      }
    },
    [history],
  );
  const handlePayClick = useCallback(
    (transaction: TransactionRowItem) => {
      payClickGuardRef.current = true;
      setSelectedPayeeCount(transaction.payeeCount);
      history.push(`${TRANSACTIONS_BASE_URL}/${transaction.id}/pay`);
    },
    [history],
  );

  const handleCreatePayment = useCallback(
    (payable: AccountingPayableData) => {
      setSelectedPayeeCount(payable.payeeCount);
      history.push(`${TRANSACTIONS_BASE_URL}/${payable.id}/pay`);
    },
    [history],
  );

  const handlePayWorkflowExit = useCallback(() => {
    setSelectedPayeeCount(null);
    history.goBack();
  }, [history]);

  const handleReviewSlideoutClose = useCallback(() => {
    history.goBack();
  }, [history]);

  const isM3Enabled = useFeatureFlag("treasuryPaymentIntegration", false);
  const clientLevelPermissionsEnabled = useFeatureFlag("treasuryClientLevelPermissions", false);

  const paymentsContext = useMemo(
    () => ({
      isGPX: false,
      hasApprovePaymentPermission: clientLevelPermissionsEnabled
        ? hasTreasuryAccessOnAnyArena(user.roles)
        : isTreasuryUser(user.roles),
      currentUserId: user.id,
      isM3Enabled,
    }),
    [user.id, user.roles, isM3Enabled, clientLevelPermissionsEnabled],
  );

  return (
    <>
      <Flex className={styles.TransactionsPageContainer}>
        <Flex className={styles.TransactionsHeader}>
          <Text variant="h2">Transactions</Text>
        </Flex>
        <TransactionsTable
          paymentsContext={paymentsContext}
          selectedTransactionId={showPayWorkflow ? undefined : transactionBatchId}
          onSelectTransaction={handleSelectTransaction}
          onCloseSlideout={handleCloseSlideout}
          onPayClick={handlePayClick}
          onCreatePayment={handleCreatePayment}
          onReviewClick={handleReviewClick}
          onPaymentBatchReviewClick={handlePaymentBatchReviewClick}
          selectedPaymentBatchId={showReviewWorkflow ? paymentBatchId : undefined}
          onReviewSlideoutClose={handleReviewSlideoutClose}
        />
      </Flex>
      {showPayWorkflow && transactionBatchId && (
        <PaymentBatchWorkflow
          batchId={transactionBatchId}
          initialStep={
            selectedPayeeCount === 1
              ? PaymentBatchStepNames.PaymentDetails
              : PaymentBatchStepNames.SelectPositions
          }
          onClose={handlePayWorkflowExit}
        />
      )}
    </>
  );
};

export default TransactionsPage;
